import React, { useEffect } from 'react'
import ReactQuill from '@capaj/react-quill'
import { merge } from 'lodash'
import '@capaj/react-quill/dist/quill.snow.css'
import { getBackgroundColor } from 'shared/colorUtils'
import * as ReactQuillNS from '../../../../../types/Quill'
import { Box } from 'front-end/src/components'
import { css } from '@emotion/react'

import './CustomQuill.less'
import { useOrganisation } from 'front-end/src/public-api/hooks/useOrganisation'

const QUILL_MODULES = {
  clipboard: {
    matchVisual: false // this is needed to prevent quill from adding extra paragraphs before/after certain html elements
  },
  history: {
    // delay: 2000,
    // maxStack: 500,
    userOnly: true // make sure history of undo manager ignores API inserts
  }
}

export interface ICustomQuillProps {
  autofocus?: boolean
  toolbarItems?: any[]
}

const CustomQuill = (
  props: ReactQuillNS.ComponentProps & ICustomQuillProps
) => {
  const { data: orgData } = useOrganisation()
  const quill = React.createRef<ReactQuill & ReactQuillNS.Mixin>()

  useEffect(() => {
    const editor = quill.current!.getEditor()

    editor.root.removeAttribute('data-gramm')

    if (props.autofocus) {
      quill.current && quill.current.focus()
    }

    // quill inserts clipboard content inside an invisible element which
    // is by default positioned at top: 50%, which doesn't work with
    // the autogrow we are using. this will move that invisible el to a correct
    // place before focusing
    editor.clipboard['container'].focus = function () {
      let node: any = document.getSelection()?.focusNode
      editor.clipboard['container'].style.top = `${
        // for text node we use its parent
        node.offsetTop || node.parentElement?.offsetTop
      }px`
      HTMLElement.prototype.focus.call(this)
    }

    // I yet don't know why the previous fix is not enough but
    // this seems to resolve the rest of the issue (jump to a start of editor)
    // it should be safe because we are focused already (just not exactly at the root)
    editor.root.focus = () => {}
  }, [])

  const defaultQuillToolbarItems = () => {
    const backgroundColor = orgData?.organisation
      ? getBackgroundColor(orgData?.organisation)
      : null

    return [
      ['bold', 'italic', 'underline'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'image'],
      [{ header: 1 }, { header: 2 }],
      [
        {
          color: [
            '#000000',
            '#e60000',
            '#ff9900',
            '#ffff00',
            '#008a00',
            '#0066cc',
            '#9933ff',
            '#ffffff',
            '#facccc',
            '#ffebcc',
            '#ffffcc',
            '#cce8cc',
            '#cce0f5',
            '#ebd6ff',
            '#bbbbbb',
            '#f06666',
            '#ffc266',
            '#ffff66',
            '#66b966',
            '#66a3e0',
            '#c285ff',
            '#888888',
            '#a10000',
            '#b26b00',
            '#b2b200',
            '#006100',
            '#0047b2',
            '#6b24b2',
            '#444444',
            '#5c0000',
            '#663d00',
            '#666600',
            '#003700',
            '#002966',
            '#3d1466',
            backgroundColor
          ]
        },
        { background: [] }
      ],
      ['clean'],
      [{ align: [] }]
    ]
  }

  const {
    modules: modulesOverride,
    children,
    value,
    readOnly,
    toolbarItems,
    ...other
  } = props

  const modules = merge({}, QUILL_MODULES, modulesOverride)
  modules.toolbar = toolbarItems ? toolbarItems : defaultQuillToolbarItems()

  // remove quill toolbar when is readOnly
  if (readOnly) {
    modules.toolbar = []
  }
  return (
    <Box
      css={css`
        .quill p a,
        .quill ul a,
        .quill ol a {
          color: var(--chakra-colors-linkText);
        }
      `}
    >
      <ReactQuill
        ref={quill}
        className={readOnly ? 'readonly' : ''}
        readOnly={readOnly}
        modules={modules}
        value={value || ''} // react-quill doesn't handle undefined and throws an error, so we protect from it here
        {...other}
        preserveWhitespace // needed to show tabs in start of the row: https://looopco.atlassian.net/browse/DEV-6422
      >
        {children}
      </ReactQuill>
    </Box>
  )
}

export default CustomQuill
